import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 whitespace-nowrap',
  {
    variants: {
      variant: {
        // ⚠ `ring-offset-2` on the DARK-filled variants is not decoration: a
        // navy ring at 30% opacity drawn directly against a navy fill is
        // invisible, so the primary action on every screen — "Approve" on the
        // timesheet roster, "Send" on an invoice — had no perceptible focus
        // indicator at all, and a keyboard user tabbing through a row could not
        // tell where they were. The offset puts the card's white ground between
        // the fill and the ring, which is what makes it readable.
        primary:
          'bg-secondary text-white hover:bg-secondary-800 active:bg-secondary-700 focus:ring-secondary/60 focus:ring-offset-2',
        destructive:
          'bg-primary text-white hover:bg-primary-600 active:bg-primary-700 focus:ring-primary/60 focus:ring-offset-2',
        outline:
          'border border-grey-300 bg-white text-secondary hover:bg-grey-50 hover:border-grey-400 focus:ring-secondary/20',
        ghost: 'bg-transparent text-secondary hover:bg-grey-100 active:bg-grey-200 focus:ring-grey-300',
        accent:
          'bg-accent text-white hover:bg-accent-700 active:bg-accent-800 focus:ring-accent/60 focus:ring-offset-2',
      },
      size: {
        sm: 'text-sm px-3.5 py-1.5',
        md: 'px-5 py-2.5 text-sm',
        lg: 'text-base px-7 py-3',
        icon: 'p-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading = false, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
